// Highcharts' UMD builds of `highcharts` and `highcharts/highmaps` both assign the same
// `window._Highcharts` global, and the UMD `highcharts/modules/*` wrappers compose themselves onto
// whatever that global points at rather than onto the namespace that imported them. Which core wins
// is therefore decided by module evaluation order across the bundle - implicit, and silently
// re-decided whenever an import moves - so the modules can end up patching a core no chart renders
// with. The ESM builds resolve their own core instead, which makes the target explicit, at the cost
// of a Highcharts instance separate from the one the UMD imports elsewhere in the app use. Charts
// that want exporting must therefore render with the instance exported from here.
import Highcharts from "highcharts/esm/highcharts";
import "highcharts/esm/modules/exporting";
import "highcharts/esm/modules/offline-exporting"; // Renders the downloads in the browser, so chart data never reaches a third party
import "highcharts/esm/modules/export-data"; // Adds the CSV/XLS menu items
import {AssetVersionPipe} from "../pipes/asset-version.pipe";

const textColor = '#4E5258';
const hoverBackgroundColor = '#EBEDEF';
const fontFamily = 'Poppins, sans-serif';

// The modules above enable the context button on every chart on this instance, so default it off and
// let each chart opt in with `exporting: chartExportingOptions(...)`. The menu styling depends on
// nothing per-chart, so it lives in the instance defaults rather than being rebuilt by every caller.
Highcharts.setOptions({
  exporting: {enabled: false},
  navigation: {
    menuStyle: {
      background: 'white',
      border: 'none',
      borderRadius: 4,
      boxShadow: '0 0 6px 0 rgba(0, 0, 0, 0.10)',
      padding: '4px 0',
    },
    menuItemStyle: {
      fontFamily: fontFamily,
      fontSize: '12.8px',
      fontWeight: '400',
      color: textColor,
      padding: '6px 12px',
    },
    menuItemHoverStyle: {
      background: hoverBackgroundColor,
      color: textColor,
    },
  },
});

// The app's download glyph as a renderer symbol, so charts can use it for the context button.
// Traced from the 20x20 path in shared/components/icons/download-tooltip-icon - which is a filled
// glyph, where this is a stroked outline - and expressed in fractions of the symbol box. A `url(...)`
// symbol pointing at svg/download.svg cannot be used instead: Highcharts sizes image symbols from a
// cache that is empty on first render, so the icon renders at its intrinsic size and overflows the
// button. Keep in sync with that component if the icon changes.
Highcharts.SVGRenderer.prototype.symbols['download'] = (x: number, y: number, w: number, h: number) => [
  // Tray, open at the top
  ['M', x + w * 0.125, y + h * 0.594],
  ['L', x + w * 0.125, y + h * 0.8125],
  ['L', x + w * 0.875, y + h * 0.8125],
  ['L', x + w * 0.875, y + h * 0.594],
  // Arrow shaft
  ['M', x + w * 0.5, y + h * 0.24],
  ['L', x + w * 0.5, y + h * 0.699],
  // Arrow head
  ['M', x + w * 0.344, y + h * 0.521],
  ['L', x + w * 0.5, y + h * 0.699],
  ['L', x + w * 0.656, y + h * 0.521],
];

// Static across charts, so built once at module load; only `filename` varies per chart.
const EXPORTING_DEFAULTS: Highcharts.ExportingOptions = {
  enabled: true,
  fallbackToExportServer: false, // Keep the chart's data in the browser instead of posting it to Highcharts' export server
  error: () => Highcharts.error(28, false), // Warn rather than throw, which is what a disabled fallback does on its own
  scale: 2, // The chart is rendered at a fixed width of 720, so scale up to keep the downloaded image sharp
  buttons: {
    contextButton: {
      symbol: 'download', // The app's download glyph, registered on the instance, in place of Highcharts' hamburger
      symbolSize: 16,
      symbolStroke: textColor,
      symbolStrokeWidth: 1.5,
      symbolFill: 'none',
      symbolX: 14, // Centres the 16px symbol in the 28px button; the defaults are tuned for the 14px hamburger
      symbolY: 14,
      menuItems: ['downloadPNG', 'separator', 'downloadCSV', 'downloadXLS'],
    }
  }
};

/**
 * Exporting options for a chart rendered with this instance. The app's font is embedded in the
 * download by the wraps below, so callers only supply the title the filename is derived from.
 */
export const chartExportingOptions = (title: string): Highcharts.ExportingOptions => ({
  ...EXPORTING_DEFAULTS,
  filename: (title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'polaris-chart',
});

// Poppins reaches the page through a Google Fonts stylesheet, but exporting re-renders the chart into
// a detached sandbox and rasterises it by drawing that SVG into a canvas as an image. Nothing in that
// context can reach the document's stylesheets or load a remote font, so the @font-face has to travel
// inside the exported SVG as a data URI. These are the same latin and latin-ext subsets the
// stylesheet requests, self-hosted so they can be read and inlined.
const EMBEDDED_FONT_UNICODE_RANGES: Record<string, string> = {
  'latin': 'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD',
  'latin-ext': 'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF',
};
const EMBEDDED_FONT_WEIGHTS = [400, 600];
const EMBEDDED_FONT_SUBSETS = Object.entries(EMBEDDED_FONT_UNICODE_RANGES).flatMap(
  ([subset, unicodeRange]) => EMBEDDED_FONT_WEIGHTS.map(weight => ({
    weight,
    unicodeRange,
    file: `poppins-${subset}-${weight}.woff2`,
  })));

// `sanitizeSVG` runs synchronously mid-export, so the CSS has to be sitting in `embeddedFontCss` by
// the time an export starts; `embeddedFontRequest` exists only to de-dupe the fetches while in flight.
let embeddedFontCss: string | undefined;
let embeddedFontRequest: Promise<void> | undefined;
const assetVersion = new AssetVersionPipe();

const readAsDataUri = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result as string);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

const loadEmbeddedFontCss = (): Promise<void> => {
  embeddedFontRequest ??= Promise.all(EMBEDDED_FONT_SUBSETS.map(async ({weight, file, unicodeRange}) => {
    const response = await fetch(assetVersion.transform(`fonts/${file}`));
    // The type is pinned rather than taken from the response, so the data URI is right whatever
    // content type the host serves .woff2 with.
    const dataUri = await readAsDataUri(new Blob([await response.arrayBuffer()], {type: 'font/woff2'}));
    return `@font-face{font-family:'Poppins';font-style:normal;font-weight:${weight};`
      + `src:url(${dataUri}) format('woff2');unicode-range:${unicodeRange};}`;
  }))
    .then(faces => {
      embeddedFontCss = faces.join('');
    })
    .catch(() => {
      embeddedFontRequest = undefined; // Let the next export try again rather than caching the failure
    });
  return embeddedFontRequest;
};

// The @font-face has to be added after Highcharts has sanitised the SVG, not before: sanitizeSVG
// normalises internal references with `.replace(/url\([^#]+#/g, 'url(#')`, and since a base64 data
// URI contains no '#' that pattern runs on past the font and swallows the markup up to the next '#'
// in the document, leaving an SVG the rasteriser cannot load. Wrapping the method Highcharts already
// calls on the way out - rather than re-implementing the download - keeps its image embedding,
// foreignObject fallbacks, filename and error handling, and covers every export type, not just PNG.
const chartProto = Highcharts.Chart.prototype as any; // `sanitizeSVG` is not in the published types
Highcharts.wrap(chartProto, 'sanitizeSVG', function (this: Highcharts.Chart, proceed: Function, ...args: unknown[]) {
  const svg = proceed.apply(this, args) as string;
  return embeddedFontCss
    ? svg.replace(/<svg[^>]*>/, match => `${match}<style>${embeddedFontCss}</style>`)
    : svg;
});

// Resolve the font up front, since the serialise-and-rasterise this feeds into is synchronous.
Highcharts.wrap(chartProto, 'exportChartLocal', function (this: Highcharts.Chart, proceed: Function, ...args: unknown[]) {
  void loadEmbeddedFontCss().then(() => proceed.apply(this, args));
});

export default Highcharts;
